import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { castDraft } from 'immer';

import { PropertyKey, unlockedColumns } from '../propertyMetadata';
import { SavedView } from '../viewUtilities';

export const noFilterView: View = {
  jobFilters: [],
  visibleColumns: unlockedColumns,
};

export type SingleOperator = '=' | '!=' | '<' | '>' | '<=' | '>=';
export type DoubleOperator = '>= <=';
export type Operator = SingleOperator | DoubleOperator;

interface SingleOperatorJobFilter {
  readonly propertyKey: PropertyKey;
  readonly operator: SingleOperator;
  readonly value: string;
}
interface DoubleOperatorJobFilter {
  readonly propertyKey: PropertyKey;
  readonly operator: DoubleOperator;
  readonly low: string;
  readonly high: string;
}
export type JobFilter = SingleOperatorJobFilter | DoubleOperatorJobFilter;

export interface View {
  readonly jobFilters: readonly JobFilter[];
  readonly visibleColumns: readonly PropertyKey[];
}

interface ViewsState {
  readonly savedViews: readonly SavedView[];
  readonly activeView: number | View | 'default';
}

const initialState: ViewsState = {
  savedViews: [],
  activeView: 'default',
};

const viewsSlice = createSlice({
  name: 'views',
  initialState,
  reducers: {
    finishDeletingView: (state, action: PayloadAction<number>) => {
      state.savedViews = state.savedViews.filter((view) => view.id !== action.payload);
      state.activeView = 'default';
    },
    finishOverwritingView: (state, action: PayloadAction<SavedView>) => {
      state.savedViews = state.savedViews.map((view) =>
        view.id === action.payload.id ? castDraft(action.payload) : view,
      );
      state.activeView = action.payload.id;
    },
    finishSavingView: (state, action: PayloadAction<SavedView>) => {
      state.savedViews.push(castDraft(action.payload));
      state.activeView = action.payload.id;
    },
    removeDefaultViewFromStore: (state) => {
      for (const view of state.savedViews) {
        view.isDefault = false;
      }
    },
    setActiveView: (state, action: PayloadAction<number | View | 'default'>) => {
      state.activeView = castDraft(action.payload);
    },
    setDefaultViewWithId: (state, action: PayloadAction<number>) => {
      for (const view of state.savedViews) {
        view.isDefault = view.id === action.payload;
      }
    },
    setViewToNoFilter: (state) => {
      state.activeView = castDraft(noFilterView);
    },
    setViews: (state, action: PayloadAction<readonly SavedView[]>) => {
      state.savedViews = castDraft(action.payload);
    },
  },
});

export const {
  finishDeletingView,
  finishOverwritingView,
  finishSavingView,
  removeDefaultViewFromStore,
  setActiveView,
  setDefaultViewWithId,
  setViewToNoFilter,
  setViews,
} = viewsSlice.actions;
export const views = viewsSlice.reducer;
